import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@cloudgenie/ui';
import { API_BASE, useAuth, hasCap } from '../AuthContext';
import { AdminChrome } from './AdminPage';

/** RUN 3 §17.28 — API-key admin UI. */

interface KeyRow {
  id: string;
  name: string;
  prefix: string;
  scopes?: string[];
  created_at?: string | null;
  last_used_at?: string | null;
  revoked_at?: string | null;
  expires_at?: string | null;
}

interface CreatedKey {
  id: string;
  key: string;        // plaintext — shown once
  name: string;
  prefix: string;
}

export default function AdminApiKeysPage() {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState<KeyRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [newName, setNewName] = useState('');
  const [newScopes, setNewScopes] = useState('cli.default');
  const [freshKey, setFreshKey] = useState<CreatedKey | null>(null);

  const canManage = user && hasCap(user, 'admin.ops');

  async function load() {
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/api-keys`, { credentials: 'include' });
      if (!r.ok) throw new Error(`${r.status}`);
      setRows(await r.json());
    } catch (e) { setError((e as Error).message); }
  }

  useEffect(() => {
    if (authLoading || !user) return;
    if (!canManage) return;
    load();
  }, [authLoading, user, canManage]);

  async function createKey() {
    if (!newName.trim()) return;
    setBusy(true); setError(null); setFreshKey(null);
    try {
      const scopes = newScopes.split(',').map((s) => s.trim()).filter(Boolean);
      const r = await fetch(`${API_BASE}/api/api-keys`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), scopes }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(typeof data.detail === 'string' ? data.detail : `${r.status}`);
      setFreshKey(data);
      setNewName('');
      load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function rotate(id: string) {
    if (!confirm('Rotate this API key? The old key will stop working immediately.')) return;
    setBusy(true); setError(null); setFreshKey(null);
    try {
      const r = await fetch(`${API_BASE}/api/api-keys/${encodeURIComponent(id)}/rotate`, {
        method: 'POST', credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(typeof data.detail === 'string' ? data.detail : `${r.status}`);
      setFreshKey(data);
      load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function revoke(id: string) {
    const reason = prompt('Reason for revoke (optional)') || '';
    if (!confirm('Revoke this API key permanently? Any client using it will start returning 401.')) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/api-keys/${encodeURIComponent(id)}`, {
        method: 'DELETE', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (!r.ok && r.status !== 204) {
        const data = await r.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : `${r.status}`);
      }
      load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  if (authLoading) {
    return <AdminChrome title="API Keys"><p className="text-center py-16 text-muted-foreground">Loading…</p></AdminChrome>;
  }
  if (!user) { window.location.href = `/login?next=${encodeURIComponent('/admin/api-keys')}`; return null; }
  if (!canManage) {
    return (
      <AdminChrome title="API Keys">
        <p className="text-center py-16 text-muted-foreground" data-testid="admin-api-keys-forbidden">
          Missing user.manage capability.
        </p>
      </AdminChrome>
    );
  }

  return (
    <AdminChrome title="API Keys">
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-8" data-testid="admin-api-keys-page">
        <Link to="/admin" className="text-sm text-muted-foreground hover:text-foreground">← Admin</Link>
        <h1 className="text-3xl font-semibold mt-2">API Keys</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Personal-access-token equivalents for the CloudGenie CLI, CI pipelines and third-party integrations.
        </p>

        {/* Create form */}
        <div className="mt-6 rounded-xl border border-border bg-card p-4" data-testid="api-key-create">
          <h2 className="text-lg font-semibold mb-3">Create a key</h2>
          <div className="flex flex-wrap items-center gap-2">
            <input placeholder="Name (e.g. CI · GitHub Actions)" value={newName} onChange={(e) => setNewName(e.target.value)}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm flex-1 min-w-[220px]"
              data-testid="api-key-name" />
            <input placeholder="scopes (comma-separated)" value={newScopes} onChange={(e) => setNewScopes(e.target.value)}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm flex-1 min-w-[180px]"
              data-testid="api-key-scopes" />
            <Button variant="primary" size="sm" onClick={createKey} disabled={busy || !newName.trim()} data-testid="api-key-create-submit">
              {busy ? 'Creating…' : 'Create key'}
            </Button>
          </div>
        </div>

        {/* Freshly-created key — shown ONCE */}
        {freshKey && (
          <div className="mt-4 rounded-xl border border-yellow-500/40 bg-yellow-500/10 p-4" data-testid="api-key-fresh">
            <div className="text-sm font-semibold text-yellow-200">
              Copy this key now — it will not be shown again.
            </div>
            <code className="mt-2 block break-all rounded-md bg-background/80 px-3 py-2 text-xs font-mono">
              {freshKey.key}
            </code>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(freshKey.key)} data-testid="api-key-copy">
                Copy
              </Button>
              <Button size="sm" variant="outline" onClick={() => setFreshKey(null)} data-testid="api-key-dismiss">
                I've saved it
              </Button>
            </div>
          </div>
        )}

        {error && <p role="alert" className="mt-3 text-sm text-red-400" data-testid="api-keys-error">{error}</p>}

        {/* Existing keys */}
        <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="api-keys-table">
            <thead className="text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Prefix</th>
                <th className="text-left px-3 py-2">Scopes</th>
                <th className="text-left px-3 py-2">Last used</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows === null && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>}
              {rows !== null && rows.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No API keys yet.</td></tr>
              )}
              {rows?.map((k) => {
                const revoked = !!k.revoked_at;
                return (
                  <tr key={k.id} className="border-t border-border/40" data-testid={`api-key-row-${k.id}`}>
                    <td className="px-3 py-2">{k.name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{k.prefix}…</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{(k.scopes || []).join(', ') || '—'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{k.last_used_at || '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      {revoked
                        ? <span className="text-red-400">Revoked</span>
                        : <span className="text-emerald-400">Active</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {!revoked && (
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => rotate(k.id)} data-testid={`api-key-rotate-${k.id}`}>Rotate</Button>
                          <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10" onClick={() => revoke(k.id)} data-testid={`api-key-revoke-${k.id}`}>Revoke</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </AdminChrome>
  );
}
